import React, { useEffect, useState, useRef, cloneElement, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FileTextIcon, CheckIcon, MailIcon, SendIcon, CalendarCheckIcon, RepeatIcon, TrendingUpIcon, ChevronLeftIcon, ChevronRightIcon, ArrowUpIcon, SignalIcon, WifiIcon, BatteryFullIcon, Plus, ClockIcon, ArrowRightIcon } from 'lucide-react';
// "Works where you work" visual — a real iPhone anchored to the BOTTOM of the
// card (its bottom rail + home indicator are in frame; the top runs off the top
// edge). On the white screen, a short Giga chat conversation plays on a loop:
// the user asks Giga to do real work, Giga acknowledges, and a result card
// appears — so the phone reads as a live demo of what Giga can do. Behind the
// phone, the task-pill marquee (Card1Background) drifts across the section.
// A wide, UNIQUE pool of tasks. The carousel is split into three rows that each
// get a DISTINCT slice of this pool, so no task ever appears in more than one
// row (no visible repeats across the field). Each carries the correct brand
// logo via its simpleicons slug.
type PhoneTask = {
  text: React.ReactNode;
  icon: string;
};
const PHONE_TASKS: PhoneTask[] = [{
  text: <>
        Organise my
        <br data-id="element-736" data-mp="260f6ejnpeoaj" />
        meetings today
      </>,
  icon: 'googlecalendar'
}, {
  text: <>
        Build my
        <br data-id="element-737" data-mp="2axozc9aaxi34" />
        operations manual
      </>,
  icon: 'notion'
}, {
  text: <>
        Boost my
        <br data-id="element-738" data-mp="1x23hcm2hjyk7" />
        Shopify sales
      </>,
  icon: 'shopify'
}, {
  text: <>
        Improve my
        <br data-id="element-739" data-mp="20zhpsbice2b5" />
        website SEO
      </>,
  icon: 'semrush'
}, {
  text: <>
        Lower my
        <br data-id="element-740" data-mp="1n3xhea08p2hz" />
        ad costs
      </>,
  icon: 'meta'
}, {
  text: <>
        Draft customer
        <br data-id="element-741" data-mp="1s30acw2y3i9l" />
        outreach messages
      </>,
  icon: 'linkedin'
}, {
  text: <>
        Grow my
        <br data-id="element-742" data-mp="3hdra8ujgr6wp" />X following
      </>,
  icon: 'x'
}, {
  text: <>
        Manage my
        <br data-id="element-743" data-mp="3mef3lmv5zzu3" />
        cash flow
      </>,
  icon: 'quickbooks'
}, {
  text: <>
        Suggest ways to
        <br data-id="element-744" data-mp="3kz2v7n60wnjf" />
        grow revenue
      </>,
  icon: 'stripe'
}, {
  text: <>
        Respond to
        <br data-id="element-745" data-mp="3fxdhfzrf1vrn" />
        my customers
      </>,
  icon: 'gmail'
}, {
  text: <>
        Update my
        <br data-id="element-746" data-mp="3b03t6nfrjq5e" />
        CRM records
      </>,
  icon: 'salesforce'
}, {
  text: <>
        Nurture my
        <br data-id="element-747" data-mp="362bt364ysxq4" />
        sales pipeline
      </>,
  icon: 'hubspot'
}, {
  text: <>
        Schedule my
        <br data-id="element-748" data-mp="7i3iv1mqj8om" />
        social posts
      </>,
  icon: 'buffer'
}, {
  text: <>
        Design my
        <br data-id="element-749" data-mp="2kk6k79ll5zb" />
        next campaign
      </>,
  icon: 'canva'
}, {
  text: <>
        Send my
        <br data-id="element-750" data-mp="3twv8ymw3jn4p" />
        email newsletter
      </>,
  icon: 'mailchimp'
}, {
  text: <>
        Recap my
        <br data-id="element-751" data-mp="3oz39n7t4gyqh" />
        team's week
      </>,
  icon: 'slack'
}, {
  text: <>
        Triage my
        <br data-id="element-752" data-mp="qaxsrh5i4alz" />
        support tickets
      </>,
  icon: 'zendesk'
}, {
  text: <>
        Reconcile my
        <br data-id="element-753" data-mp="lhblk4i3wyxk" />
        monthly books
      </>,
  icon: 'xero'
}];
// Split the pool into N non-overlapping row slices (no task appears twice
// across rows). Mobile uses 3 rows to fill the taller card (the phone is
// hidden there); tablet/desktop use 2 rows alongside the phone.
function splitRows(n: number): PhoneTask[][] {
  const per = Math.ceil(PHONE_TASKS.length / n);
  return Array.from({
    length: n
  }, (_, i) => PHONE_TASKS.slice(i * per, (i + 1) * per));
}
const TASK_ROWS_2 = splitRows(2);
const TASK_ROWS_3 = splitRows(3);
function TaskPill({
  task
}: {
  task: PhoneTask;
}) {
  return <div className="flex items-center gap-4 bg-white rounded-2xl px-4 py-3 shadow-sm border border-neutral-200/60 shrink-0" data-id="element-754" data-mp="1gpx3qwi133oe">
      <span className="text-[13px] font-medium text-neutral-400 leading-snug" data-id="element-755" data-mp="1lksy43aqm34o">
        {task.text}
      </span>
      <img src={`https://cdn.simpleicons.org/${task.icon}`} alt="" className="w-5 h-5 object-contain shrink-0" loading="lazy" onError={e => {
      // Graceful fallback so a missing simpleicons slug never shows a
      // broken-image box: try the dashboard-icons CDN, then hide cleanly.
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${task.icon}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-756" data-mp="1vgw7nu7x541n" />
    </div>;
}
// The task carousel. Each row is a distinct slice of the task pool and drifts
// at a slightly different slow speed in an alternating direction. It fills the
// FULL width of its band edge-to-edge — the headline sits ABOVE this band, so
// the two never overlap. Directions/durations support up to 3 rows.
const ROW_DIRECTIONS = [['-50%', '0%'], ['0%', '-50%'], ['-50%', '0%']] as const;
const ROW_DURATIONS = [80, 70, 90];
// One drifting marquee row (its slice duplicated once for a seamless loop —
// the only repetition; no task is shared across rows).
function MarqueeRow({
  row,
  r,
  reduced
}: {
  row: PhoneTask[];
  r: number;
  reduced: boolean;
}) {
  const loop = [...row, ...row];
  return <motion.div className="flex gap-4 w-max" animate={reduced ? undefined : {
    x: [ROW_DIRECTIONS[r][0], ROW_DIRECTIONS[r][1]]
  }} transition={reduced ? undefined : {
    duration: ROW_DURATIONS[r],
    repeat: Infinity,
    ease: 'linear'
  }} data-id="element-757" data-mp="azg7a3x65dq3">
      {loop.map((task, i) => <TaskPill key={i} task={task} data-id="element-758" data-mp="13mnq0l18jyt" data-mpi={i} />)}
    </motion.div>;
}
function Card1Background() {
  const reduced = !!useReducedMotion();
  return (
    // Soft edge-fade mask restored — the drifting task pills dissolve gently
    // into the card's surface at the left and right edges rather than getting
    // hard-clipped, so the rows read as an endless marquee fading off-screen.
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" style={{
      WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)',
      maskImage: 'linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)'
    }} data-id="element-759" data-mp="jythiyt4a6bv">
      {/* Mobile: 3 rows START right below the headline (justify-start, not
         centered/bottom-pinned) so there's no dead gap between the copy and
         the pills. */}
      <div className="absolute inset-x-0 top-[120px] bottom-0 flex flex-col justify-start gap-3 pt-3 sm:hidden" data-id="element-760" data-mp="1db7vfxkh2k0h">
        {TASK_ROWS_3.map((row, r) => <MarqueeRow key={r} row={row} r={r} reduced={reduced} data-id="element-761" data-mp="18f0ny0cfh0gf" data-mpi={r} />)}
      </div>
      {/* Tablet / desktop: 2 rows sitting in the LOWER band of the card, so the
                                                                    headline can breathe in the top-left and the pills read as a quiet
                                                                    under-layer (matching the reference). */}
      <div className="absolute inset-x-0 bottom-0 hidden flex-col justify-end gap-4 pb-16 md:pb-20 sm:flex" data-id="element-762" data-mp="pkbxsjtc4ulb">
        {TASK_ROWS_2.map((row, r) => <MarqueeRow key={r} row={row} r={r} reduced={reduced} data-id="element-763" data-mp="fnpe3ycqw9kv" data-mpi={r} />)}
      </div>
    </div>
  );
}
// A dynamic, premium group chat scene that demonstrates Giga's multiplayer
// capabilities. It features a conversation between a colleague (Marcus), the
// user (Faith), and Giga. The animation is snappy and fills the screen.
type ChatStep = {
  id: string;
  sender: 'marcus' | 'faith' | 'giga';
  text?: React.ReactNode;
  isResult?: boolean;
};
const CHAT_SCRIPT: ChatStep[] = [{
  id: '1',
  sender: 'marcus',
  text: 'A few clients still haven\u2019t paid last month\u2019s invoices.'
}, {
  id: '2',
  sender: 'faith',
  text: <>
        <span className="font-semibold text-[#EA580C]" data-id="element-764" data-mp="2dkpesvdd1wt1">@Giga</span> chase the
        overdue ones and send a friendly reminder to each.
      </>
}, {
  id: '3',
  sender: 'giga',
  text: 'On it \u2014 I\u2019ll get back to you in a few. \ud83d\ude4c'
}, {
  id: '4',
  sender: 'giga',
  text: 'All done! \u2705 Followed up with everyone. Here\u2019s where things stand \ud83d\udc47'
}, {
  id: '5',
  sender: 'giga',
  isResult: true
}];
// Giga's avatar — the brand flower mark in white on a solid Giga-orange circle,
// so the assistant always reads as Giga (not a generic AI sparkle).
function GigaAvatar({
  size = 24,
  square = false
}: {
  size?: number;
  square?: boolean;
}) {
  return <span className={`flex shrink-0 items-center justify-center bg-[#EA580C] ${square ? 'rounded-[7px]' : 'rounded-full'}`} style={{
    width: size,
    height: size
  }} data-id="element-765" data-mp="x7tpzgxs2q4s">
      <span className="bg-white" style={{
      width: size * 0.56,
      height: size * 0.56,
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-766" data-mp="1678dc7inekl2" />
    </span>;
}
function TypingDots() {
  return <span className="flex items-center gap-1" data-id="element-767" data-mp="1ky7mpkrb5d5p">
      {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-neutral-400" animate={{
      opacity: [0.4, 1, 0.4],
      y: [0, -3, 0]
    }} transition={{
      duration: 0.8,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: i * 0.15
    }} data-id="element-768" data-mp="23kovy2mow2j1" />)}
    </span>;
}
// Giga's deliverable — a downloadable report file card (like a real chat
// attachment) with a headline metric, so the result reads as concrete work
// Giga produced, not just a status line.
function ResultBubble() {
  return <div className="flex w-full max-w-[260px] flex-col gap-3 rounded-2xl border border-neutral-200/80 bg-white p-3.5 shadow-[0_8px_24px_-8px_rgba(43,33,26,0.15)]" data-id="element-769" data-mp="1plfd0v73mubd">
      {/* File row */}
      <div className="flex items-center gap-3.5" data-id="element-770" data-mp="1fuuk9oe3wk0d">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EA580C] shadow-sm" data-id="element-771" data-mp="1fvm27wes6j86">
          <FileTextIcon size={20} strokeWidth={2.5} className="text-white" data-id="element-772" data-mp="11xxhcokprc34" />
        </span>
        <span className="flex min-w-0 flex-col leading-tight" data-id="element-773" data-mp="16wqvmj7tr74c">
          <span className="truncate text-[14px] font-bold text-neutral-900" data-id="element-774" data-mp="1l8enu4xt3fre">
            Overdue_Invoices.pdf
          </span>
          <span className="mt-1 text-[12px] font-medium text-neutral-500" data-id="element-775" data-mp="1pcdza948bodw">
            PDF · 6 reminders sent
          </span>
        </span>
      </div>
      {/* Headline metric pulled from the report — reminders were SENT, not
                                                                                                  collected. People can't pay instantly, so we surface the total value
                                                                                                  Giga chased up rather than implying money already came back. */}
      <div className="flex flex-col gap-1 rounded-xl bg-[#FCF3EC] px-3.5 py-2.5" data-id="element-776" data-mp="1z8gxx4zvh4w3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#EA580C]/70" data-id="element-777" data-mp="2mz340qqbgf3g">
          Followed up for
        </span>
        <span className="flex items-center gap-1.5 text-[15px] font-bold text-[#EA580C]" data-id="element-778" data-mp="2rqbgjvmy0gku">
          <TrendingUpIcon size={16} strokeWidth={2.5} data-id="element-779" data-mp="g9j52udbmpaz" />
          $12,400 total
        </span>
      </div>
    </div>;
}
// The conversation builds LIVE from an empty thread each cycle: Marcus's
// question appears first (1), then Faith's @Giga request (2), then Giga
// "types" and replies (3), then delivers the result file (4) — so the whole
// exchange reads as it unfolding in real time. With reduced motion the full
// script renders at once.
function PhoneChatScene() {
  const reduced = !!useReducedMotion();
  const [visible, setVisible] = useState(reduced ? CHAT_SCRIPT.length : 0);
  const [typing, setTyping] = useState(false);
  // The phone renders at full scale from the `sm` breakpoint up (scale-100),
  // but at 0.78 on mobile — so the same conversation column is physically
  // taller on desktop and Giga's final result card falls further down the
  // crop. Track the breakpoint so the scroll-up lift can be larger on desktop
  // (bringing the result fully into frame) while staying gentle on mobile.
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 640px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  // Once Giga's report card (the final step) lands, gently lift the whole
  // conversation column up so the earlier messages slide out of the top-half
  // crop and the full report card scrolls into the visible viewport (true chat
  // behaviour), instead of being clipped off the bottom edge.
  // With reduced motion the full script renders at once, so lift immediately.
  const scrollUp = reduced || visible >= CHAT_SCRIPT.length;
  // Lift just enough that Giga's most recent reply bubble stays comfortably in
  // frame alongside the result card — NOT so far that the earlier messages are
  // yanked all the way off-screen. A smaller offset keeps the conversation
  // feeling anchored rather than jumping to the top. On desktop the phone is
  // full-scale, so the content sits lower in the crop and needs a larger lift
  // for the full result card to clear the bottom edge.
  const scrollUpDistance = isDesktop ? 250 : 150;
  useEffect(() => {
    if (reduced) return;
    let timers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;
    const run = () => {
      // Start from an empty thread and build it message by message.
      setVisible(0);
      setTyping(false);
      let t = 500;
      // Marcus asks the question
      timers.push(setTimeout(() => {
        if (!cancelled) setVisible(1);
      }, t));
      t += 1050;
      // Faith @mentions Giga
      timers.push(setTimeout(() => {
        if (!cancelled) setVisible(2);
      }, t));
      t += 950;
      // Giga starts typing
      timers.push(setTimeout(() => {
        if (!cancelled) setTyping(true);
      }, t));
      t += 1100;
      // Giga acknowledges — "I'll get back to you in a few minutes."
      timers.push(setTimeout(() => {
        if (cancelled) return;
        setTyping(false);
        setVisible(3);
      }, t));
      t += 1400;
      // Giga goes off to do the work, then starts typing again
      timers.push(setTimeout(() => {
        if (!cancelled) setTyping(true);
      }, t));
      t += 1100;
      // Giga returns with the "all done" follow-up
      timers.push(setTimeout(() => {
        if (cancelled) return;
        setTyping(false);
        setVisible(4);
      }, t));
      t += 600;
      // Giga delivers the report file
      timers.push(setTimeout(() => {
        if (cancelled) return;
        setVisible(5);
      }, t));
      t += 2800;
      // Loop
      timers.push(setTimeout(() => {
        if (!cancelled) run();
      }, t));
    };
    const start = setTimeout(run, 600);
    return () => {
      cancelled = true;
      clearTimeout(start);
      timers.forEach(clearTimeout);
    };
  }, [reduced]);
  return <div className="flex h-full w-full flex-col bg-white" data-id="element-780" data-mp="1an7qadppqdr8">
      {/* iOS status bar — time on the left, signal / wi-fi / battery on the
                                                                                                                                                                                                right — sitting above the chat header and clearing the Dynamic Island,
                                                                                                                                                                                                so the top of the screen reads unmistakably as a live iPhone.
                                                                                                                                                                                                NOTE: a SOLID background here (no backdrop-filter) — a backdrop-blur
                                                                                                                                                                                                on this top element breaks the parent's rounded overflow clip in
                                                                                                                                                                                                most browsers, which was leaking square grey corners over the
                                                                                                                                                                                                bezel. */}
      <div className="flex items-center justify-between bg-white px-8 pt-3.5 pb-1" data-id="element-781" data-mp="35qnm06pjel8y">
        <span className="text-[14px] font-semibold text-neutral-900 leading-none" data-id="element-782" data-mp="3aq0ilj67n6oq">
          9:41
        </span>
        <div className="flex items-center gap-1.5 text-neutral-900" data-id="element-783" data-mp="3eqaoywxwzz44">
          <SignalIcon size={16} strokeWidth={2.5} data-id="element-784" data-mp="133qp142xfwre" />
          <WifiIcon size={16} strokeWidth={2.5} data-id="element-785" data-mp="134ib9oamwi50" />
          <BatteryFullIcon size={22} strokeWidth={2} data-id="element-786" data-mp="z256p37miq9t" />
        </div>
      </div>
      {/* Slack-style channel header: back chevron on the left, the channel
                                                                                                          name with a # prefix on the LEFT (not centered), the small member
                                                                                                          count beneath it, and a stacked row of member avatars pinned to the
                                                                                                          right — the layout Slack's mobile app uses at the top of a channel. */}
      <div className="relative flex items-center border-b border-neutral-200/80 bg-white px-3 pb-2.5 pt-2 z-10" data-id="element-787" data-mp="180pdp73tnbi7">
        <ChevronLeftIcon size={22} strokeWidth={2.75} className="mr-1 shrink-0 text-neutral-500" data-id="element-788" data-mp="20rhaa5osu262" />

        <div className="flex min-w-0 flex-1 flex-col leading-tight" data-id="element-789" data-mp="193weevtbd9fh">
          <div className="flex items-center gap-0.5 text-[16px] font-extrabold text-[#1D1C1D]" data-id="element-790" data-mp="192l9uts1nxnb">
            <span className="text-neutral-400 font-bold" data-id="element-791" data-mp="1dvxm04fjebz6">#</span>
            growth-team
          </div>
          <span className="mt-0.5 text-[12px] font-medium text-neutral-500" data-id="element-792" data-mp="1052gn248yd0c">
            3 members
          </span>
        </div>
        {/* Member avatars pinned right, Slack's rounded-square style */}
        <div className="flex shrink-0 -space-x-1.5" data-id="element-793" data-mp="1rsb1x94fa5bj">
          <div className="z-30 flex h-7 w-7 items-center justify-center rounded-[7px] border-2 border-white bg-[#E7DFD5] text-[11px] font-bold text-[#6B5D52]" data-id="element-794" data-mp="1wtql0vn1i1ra">
            M
          </div>
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/ChatGPT_Image_Apr_24,_2026,_12_02_55_PM_3.webp" alt="Faith" className="z-20 h-7 w-7 rounded-[7px] border-2 border-white object-cover" data-id="element-795" data-mp="1nsaljixrjk1u" />
          <div className="z-10 overflow-hidden rounded-[7px] border-2 border-white" data-id="element-796" data-mp="3wd1uk9acgxq">
            <GigaAvatar size={24} square data-id="element-797" data-mp="3w0fhxu7hp0ob" />
          </div>
        </div>
      </div>

      {/* Conversation Area — top-aligned under the header so the seed messages
                                                                                                                                                                                                          read in the visible "top half" of the phone. The inner column is
                                                                                                                                                                                                          translated UP once Giga's report card lands, so the full report
                                                                                                                                                                                                          scrolls into the visible viewport instead of being clipped off the
                                                                                                                                                                                                          bottom edge of the crop. */}
      <div className="relative flex flex-1 flex-col justify-start overflow-hidden px-0 pt-4" data-id="element-798" data-mp="1v0vejptk886w">
        <motion.div className="flex flex-col gap-4" animate={{
        y: scrollUp ? -scrollUpDistance : 0
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 34
      }} data-id="element-799" data-mp="1uz0vyi5rgs5z">
          {/* Slack-style timestamp divider — centered "Today" pill on a hairline rule. */}
          <AnimatePresence data-id="element-800" data-mp="2drd5bouu0tpm">
            {(reduced || visible >= 1) && <motion.div key="timestamp" className="relative flex justify-center pt-2 pb-2" initial={reduced ? false : {
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-801" data-mp="2drwby9f17voc">
                <div className="absolute inset-0 flex items-center px-4" data-id="element-802" data-mp="2v179eufw5csb">
                  <div className="w-full border-t border-neutral-200" data-id="element-803" data-mp="2zyqn9t8ofnjq"></div>
                </div>
                <span className="relative bg-white px-3 text-[12px] font-bold text-[#1D1C1D] border border-neutral-200 rounded-full py-1 shadow-sm" data-id="element-804" data-mp="2lbnnffwvt2t8">
                  Today
                </span>
              </motion.div>}
          </AnimatePresence>

          <AnimatePresence initial={false} data-id="element-805" data-mp="2ccacsjx0u86x">
            {CHAT_SCRIPT.slice(0, visible).map(step => {
            const isMarcus = step.sender === 'marcus';
            const isFaith = step.sender === 'faith';
            const isGiga = step.sender === 'giga';
            const senderName = isMarcus ? 'Marcus' : isFaith ? 'Faith' : 'Giga';
            return <motion.div key={step.id} layout initial={reduced ? false : {
              opacity: 0,
              y: 15,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 28
            }} className="flex w-full justify-start px-4" data-id="element-806" data-mp="kdetfhou23q">
                  <div className="flex w-full items-start gap-3" data-id="element-807" data-mp="3qaavn58pv01m">
                    {isMarcus && <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-[#E7DFD5] text-[14px] font-bold text-[#6B5D52]" data-id="element-808" data-mp="3lbr9b3q7nm8q">
                        M
                      </div>}
                    {isFaith && <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/ChatGPT_Image_Apr_24,_2026,_12_02_55_PM_3.webp" alt="Faith" className="h-9 w-9 shrink-0 rounded-[8px] object-cover" data-id="element-809" data-mp="huhfwsm7lslm" />}
                    {isGiga && <div className="rounded-[8px] overflow-hidden shrink-0" data-id="element-810" data-mp="15kbpn1b8f11n">
                        <GigaAvatar size={36} square data-id="element-811" data-mp="10sl9gepm5j2b" />
                      </div>}
                    <div className="flex min-w-0 flex-1 flex-col" data-id="element-812" data-mp="191r5d67ymw01">
                      <div className="flex items-baseline gap-2 leading-tight" data-id="element-813" data-mp="193kdkpy81oa5">
                        <span className="text-[15px] font-extrabold text-[#1D1C1D]" data-id="element-814" data-mp="1d3kx2p06gpo2">
                          {senderName}
                        </span>
                        <span className="text-[12px] font-medium text-neutral-500" data-id="element-815" data-mp="1n1s3wf0r5rcc">
                          9:41 AM
                        </span>
                      </div>
                      {!step.isResult && <div className="mt-0.5 text-[15px] font-normal leading-snug text-[#1D1C1D]" data-id="element-816" data-mp="1w1f5b0thex5e">
                          {step.text}
                        </div>}
                      {isGiga && step.isResult && <div className="mt-2 w-full" data-id="element-817" data-mp="25rgrfn29y5ho">
                          <ResultBubble data-id="element-818" data-mp="25qyns5da8ozq" />
                        </div>}
                    </div>
                  </div>
                </motion.div>;
          })}
          </AnimatePresence>

          {/* Typing indicator */}
          <AnimatePresence data-id="element-819" data-mp="2fnjtyu11g655">
            {typing && !reduced && <motion.div key="typing" layout initial={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9,
            transition: {
              duration: 0.15
            }
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 28
          }} className="flex justify-start px-4" data-id="element-820" data-mp="3cg9muarc6gh7">
                <div className="flex items-start gap-3" data-id="element-821" data-mp="2oknlj1lnigu3">
                  <div className="rounded-[8px] overflow-hidden shrink-0" data-id="element-822" data-mp="32ivhp7av5e47">
                    <GigaAvatar size={36} square data-id="element-823" data-mp="32gsgdr49fyk6" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col" data-id="element-824" data-mp="2yfyuf3adottx">
                    <div className="flex items-baseline gap-2 leading-tight" data-id="element-825" data-mp="hs4oogxukppk">
                      <span className="text-[15px] font-extrabold text-[#1D1C1D]" data-id="element-826" data-mp="drksbfl3lq4h">
                        Giga
                      </span>
                    </div>
                    <div className="mt-1.5 flex h-5 items-center" data-id="element-827" data-mp="lcwrpsurbhy9">
                      <TypingDots data-id="element-828" data-mp="qaoi2ifr6w0c" />
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Slack-style message input: a bordered rounded box with formatting icons
                                                                                                          and a send button. */}
      <div className="bg-white px-3 pb-8 pt-2" data-id="element-829" data-mp="18z38b3i8y603">
        <div className="flex flex-col rounded-xl border border-neutral-300 bg-white p-2 shadow-sm" data-id="element-830" data-mp="ubi6dcx3yctr">
          <span className="px-2 pb-6 pt-1 text-[15px] text-neutral-400" data-id="element-831" data-mp="zalcz1sqkr3u">
            Message #growth-team
          </span>
          <div className="flex items-center justify-between" data-id="element-832" data-mp="1mznr3zo5w1tl">
            <div className="flex items-center gap-3 px-2 text-neutral-400" data-id="element-833" data-mp="1mycnq0ymow30">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-834" data-mp="1rsgyywexfq9w">
                <path d="M12 5v14M5 12h14" data-id="element-835" data-mp="1wn9s6zwxv70z" />
              </svg>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-836" data-mp="1wlyt3defung7">
                <path d="M4 7V4h16v3M9 20h6M12 4v16" data-id="element-837" data-mp="1nmbq4n6299l1" />
              </svg>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-838" data-mp="1iojubi2ap3bk">
                <circle cx="12" cy="12" r="10" data-id="element-839" data-mp="1dv7dhu3uy0td" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" data-id="element-840" data-mp="18xfgilocvoib" />
              </svg>
            </div>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-400" data-id="element-841" data-mp="14xn603fej8ab">
              <SendIcon size={16} className="ml-0.5" data-id="element-842" data-mp="zwzjnv24bfmf" />
            </span>
          </div>
        </div>
      </div>
    </div>;
}
function WorksWhereYouWorkVisual() {
  // A real-size iPhone (device width ~330px, true to life) shown as its full
  // TOP HALF: the device is CENTERED horizontally so its whole width is in
  // frame, and anchored to the TOP of the card so the status bar, chat header
  // and the first messages read clearly. The device is much taller than the
  // card, so its BOTTOM runs off the bottom edge (cropped there — never on the
  // side). Because the device is rendered at (near) real dimensions and NOT
  // scaled down, the chat UI reads at true-to-life mobile scale.
  return <div className="absolute inset-0 overflow-hidden" data-id="element-843" data-mp="2suwckzvdciix">
      {/* The phone is anchored to the RIGHT of the card, large and true-to-life,
                                                              with its TOP just below the card's top edge (status bar + chat header in
                                                              frame) and its BOTTOM running off the card's bottom edge (cropped there,
                                                              never the top). origin-top-right keeps it pinned to the right as it
                                                              scales across breakpoints, matching the reference layout. */}
      <div className="absolute right-2 md:right-12 lg:right-20 top-[40px] w-[360px] h-[980px] scale-[0.82] md:scale-[0.9] lg:scale-100 origin-top-right" data-id="element-844" data-mp="1chsdm1b1skn6">
        {/* Single thin, even titanium frame — one consistent bezel width all
          the way around (no chunky stacked bands). The screen is nested just
          inside it, so the phone reads as modern edge-to-edge hardware. */}
        <div className="relative h-full w-full rounded-[3.3rem] p-[3px] shadow-[0_24px_60px_-16px_rgba(43,33,26,0.5)]" style={{
        background: 'linear-gradient(150deg, #6E6E73 0%, #2B2B2F 30%, #0A0A0B 62%, #3A3A40 100%)'
      }} data-id="element-845" data-mp="1v7h2pds7x6um">
          {/* Concentric radii: each nested layer's radius = its parent's radius
           minus the parent's 3px inset, so the corners nest perfectly and the
           light frame gradient can never peek through as silver wedges.
           Frame 3.3rem → black band 3.1rem → screen 2.9rem. */}
          <div className="relative h-full w-full rounded-[3.1rem] bg-black p-[3px]" data-id="element-846" data-mp="2nsxcss24inke">
            {/* The screen — overflow-hidden clips the chat UI to the rounded
             device corners so nothing pokes past the bezel. */}
            <div className="relative flex h-full w-full flex-col rounded-[2.9rem] bg-white overflow-hidden" data-id="element-847" data-mp="2evn1j6pb2yfh">
              {/* Dynamic Island — the black pill that identifies this instantly
               as a modern iPhone, floating over the top of the screen. */}
              <div className="pointer-events-none absolute left-1/2 top-2 z-30 h-[26px] w-[100px] -translate-x-1/2 rounded-full bg-black" data-id="element-848" data-mp="1r595z67iihg5" />
              <PhoneChatScene data-id="element-849" data-mp="1v36vyb93x7kl" />
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// "Connects in seconds" visual — modeled on the uploaded reference: full-color
// app tiles on soft rounded-white chips, scattered across the field riding on
// faint dashed orbital arcs that all sweep toward the BOTTOM-RIGHT corner,
// where the Giga mark sits as the orbit's anchor (every tool orbiting in to
// Giga). Tiles vary in size for depth and float gently. Business + social
// tools Giga plugs into.
const CONNECT_TILES = [
// size: tile px. Positions are tuned for an EVEN, airy scatter across the
// whole field (roughly three loose rows) with gentle size variation for
// depth — they still drift toward the Giga hub in the lower-right, but the
// field reads balanced rather than clumped in the upper-left. The bottom-
// right corner is intentionally left light because the Giga hub sits there.
// A broad mix of the real SaaS / business tools founders and teams run on.
// — top row —
{
  slug: 'asana',
  name: 'Asana',
  size: 50,
  top: '6%',
  left: '8%',
  rotate: -6,
  delay: 0.2
}, {
  slug: 'shopify',
  name: 'Shopify',
  size: 56,
  top: '2%',
  left: '40%',
  rotate: -4,
  delay: 0
}, {
  slug: 'stripe',
  name: 'Stripe',
  size: 46,
  top: '8%',
  left: '74%',
  rotate: 6,
  delay: 0.5
},
// — middle row —
{
  slug: 'gmail',
  name: 'Gmail',
  size: 44,
  top: '34%',
  left: '24%',
  rotate: 4,
  delay: 0.9
}, {
  slug: 'salesforce',
  name: 'Salesforce',
  size: 52,
  top: '38%',
  left: '54%',
  rotate: -5,
  delay: 1.2
}, {
  slug: 'notion',
  name: 'Notion',
  size: 44,
  top: '32%',
  left: '84%',
  rotate: 5,
  delay: 0.7
},
// — bottom row —
{
  slug: 'quickbooks',
  name: 'QuickBooks',
  size: 50,
  top: '64%',
  left: '6%',
  rotate: 6,
  delay: 0.3
}, {
  slug: 'googlesheets',
  name: 'Google Sheets',
  size: 46,
  top: '68%',
  left: '36%',
  rotate: -4,
  delay: 1.4
}, {
  slug: 'hubspot',
  name: 'HubSpot',
  size: 44,
  top: '62%',
  left: '64%',
  rotate: 4,
  delay: 1.0
}, {
  slug: 'zendesk',
  name: 'Zendesk',
  size: 42,
  top: '46%',
  left: '40%',
  rotate: -5,
  delay: 0.6
}];
function ConnectTile({
  tile,
  reduced
}: {
  tile: (typeof CONNECT_TILES)[number];
  reduced: boolean;
}) {
  const radius = tile.size >= 60 ? 18 : tile.size >= 50 ? 15 : 12;
  return <motion.div className="absolute bg-white shadow-[0_16px_34px_-14px_rgba(43,33,26,0.30)] ring-1 ring-black/[0.04] flex items-center justify-center" style={{
    top: tile.top,
    left: tile.left,
    width: tile.size,
    height: tile.size,
    borderRadius: radius,
    rotate: `${tile.rotate}deg`
  }} initial={{
    opacity: 0,
    scale: 0.6
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: tile.delay * 0.25,
    type: 'spring',
    stiffness: 220,
    damping: 17
  }} data-id="element-850" data-mp="201nxdtr50ats">
      <motion.img src={`https://cdn.simpleicons.org/${tile.slug}`} alt={tile.name} className="object-contain" style={{
      width: tile.size * 0.52,
      height: tile.size * 0.52
    }} loading="lazy" onError={e => {
      const el = e.currentTarget as HTMLImageElement;
      if (!el.dataset.fallback) {
        el.dataset.fallback = '1';
        el.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tile.slug}.svg`;
      } else {
        el.style.visibility = 'hidden';
      }
    }} animate={reduced ? undefined : {
      y: [0, -6, 0]
    }} transition={reduced ? undefined : {
      duration: 4 + tile.delay,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: tile.delay
    }} data-id="element-851" data-mp="12exfxhk1ika9" />
    </motion.div>;
}
// The Giga mark anchored in the bottom-right corner — the orbital centre that
// every app tile arcs toward. Kept STATIC and flat: no perpetual float and no
// glow/halo, per request, so it simply sits as the solid hub of the
// constellation (it still fades/scales in once on first reveal).
function ConnectsHub({
  reduced
}: {
  reduced: boolean;
}) {
  return <motion.div className="absolute -bottom-3 -right-3 z-10 flex items-center justify-center" initial={{
    opacity: 0,
    scale: 0.5
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.2,
    type: 'spring',
    stiffness: 200,
    damping: 16
  }} data-id="element-852" data-mp="1dt8samj0541l">
      <div className="relative w-[68px] h-[68px] flex items-center justify-center" data-id="element-853" data-mp="3h3c5mcvaylcl">
        {/* The Giga flower on its own — no orange tile, no glow. The mark is
         filled with a FLAT brand orange (#EA580C, the same orange as the "Try
         Giga for free" buttons) using the PNG silhouette as a CSS mask, so it's
         a clean solid color with no gradient and no drop shadow. */}
        <span role="img" aria-label="Giga" className="w-12 h-12 bg-[#EA580C]" style={{
        WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
        maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain'
      }} data-id="element-854" data-mp="2ycky7guvl50u" />
      </div>
    </motion.div>;
}
function ConnectsVisual() {
  const reduced = !!useReducedMotion();
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-855" data-mp="11m4k7b6b98px">
      {/* Concentric dashed orbital arcs the tiles ride on — anchored to the
                                                                                                                                                                                                                                                                                                                                                                                                                    BOTTOM-RIGHT corner (the Giga hub) so every arc sweeps inward toward
                                                                                                                                                                                                                                                                                                                                                                                                                    it, matching the reference's converging-orbit composition. */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-856" data-mp="3mi89p8o0xssx">
        {[34, 54, 76, 98].map((r, i) => <circle key={r} cx="100" cy="104" r={r} fill="none" stroke="#EA580C" strokeOpacity={0.38 - i * 0.05} strokeWidth="0.9" strokeDasharray="1.6 2.4" vectorEffect="non-scaling-stroke" data-id="element-857" data-mp="3chxujiifibb4" data-mpi={i} />)}
      </svg>
      {/* The absolutely-positioned tiles are laid out in % across the card. On
                                                                                                                                                                                                                                                                                                                                                                                      a narrow mobile card those fixed-px tiles crowd and collide, so we scale
                                                                                                                                                                                                                                                                                                                                                                                      the whole constellation down a touch on small screens (origin top-center)
                                                                                                                                                                                                                                                                                                                                                                                      so it stays airy and uncluttered; full size from sm up. */}
      <div className="absolute inset-0 origin-top scale-[0.82] sm:scale-100" data-id="element-858" data-mp="39yugbffeih8w">
        {CONNECT_TILES.map(tile => <ConnectTile key={tile.slug} tile={tile} reduced={reduced} data-id="element-859" data-mp="17cff78bjk4yq" />)}
      </div>
      <ConnectsHub reduced={reduced} data-id="element-860" data-mp="1c9xr97kuv0ay" />
    </div>;
}
// "Acts like a partner" visual — a DENSE pill-cloud (modeled on the uploaded
// reference layout): a field of faint grey ghost pills (the endless busy-work)
// tilted and bleeding off the card edges, with three vivid accent pills
// floating in front — the work Giga actually picks up. The accent pills use
// ONLY Giga's palette (warm orange family + dark brown), not the reference's
// purple/pink/green, which aren't part of our system.
const GHOST_PILLS = [{
  text: 'tips',
  top: '6%',
  left: '40%',
  rotate: -4
}, {
  text: 'content',
  top: '2%',
  left: '72%',
  rotate: 5
}, {
  text: 'goals',
  top: '20%',
  left: '-8%',
  rotate: -7
}, {
  text: 'reals',
  top: '24%',
  left: '74%',
  rotate: 6
}, {
  text: 'statistics',
  top: '44%',
  left: '-6%',
  rotate: 4
}, {
  text: 'research',
  top: '46%',
  left: '82%',
  rotate: -5
}, {
  text: 'new ideas',
  top: '74%',
  left: '14%',
  rotate: -4
}, {
  text: 'captions',
  top: '70%',
  left: '-10%',
  rotate: 6
}, {
  text: 'recaps',
  top: '88%',
  left: '64%',
  rotate: 5
}];
const ACCENT_PILLS = [{
  text: 'replies',
  top: '20%',
  left: '14%',
  rotate: -6,
  bg: 'bg-[#F59E5B]',
  delay: 0
}, {
  text: 'nudges',
  top: '42%',
  left: '32%',
  rotate: 5,
  bg: 'bg-[#EA580C]',
  delay: 0.12
}, {
  text: 'notices',
  top: '64%',
  left: '46%',
  rotate: -5,
  bg: 'bg-[#C2410C]',
  delay: 0.24
}];
function PartnerVisual() {
  const reduced = !!useReducedMotion();
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-861" data-mp="c1r85zm7ed4r">
      {/* The pill cloud is laid out with absolute % positions tuned for the
                                                                                                                                                                                                                                                                                                                                                                                      desktop card. On a narrow mobile card the bigger accent pills overlap
                                                                                                                                                                                                                                                                                                                                                                                      hard and a few ghost pills bleed messily off-frame, so we scale the
                                                                                                                                                                                                                                                                                                                                                                                      whole cloud down a touch on small screens (origin top-center) to keep
                                                                                                                                                                                                                                                                                                                                                                                      it legible and tidy; full size from sm up. */}
      <div className="absolute inset-0 origin-top scale-[0.8] sm:scale-100" data-id="element-862" data-mp="3a0nst3mykhrl">
        {/* Ghost layer — dense, faint, tilted pills, several bleeding past the
         card edges so the cloud feels deeper and busier than the frame. */}
        {GHOST_PILLS.map((pill, i) => <motion.span key={pill.text} className="absolute whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold text-neutral-300 bg-neutral-100" style={{
        top: pill.top,
        left: pill.left,
        rotate: `${pill.rotate}deg`
      }} initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.04,
        duration: 0.4
      }} data-id="element-863" data-mp="3jstk0b5dyv5q" data-mpi={i}>
            {pill.text}
          </motion.span>)}
        {/* Accent layer — the vivid pills Giga picks up: larger, bolder, and
         floating above the ghost field. */}
        {ACCENT_PILLS.map(pill =>
      // The whole pill floats; the text inside stays put relative to it.
      // The continuous bob lives on a dedicated wrapper so it doesn't fight
      // the entrance spring (initial/whileInView) on the same element.
      <motion.span key={pill.text} className="absolute z-10" style={{
        top: pill.top,
        left: pill.left
      }} initial={{
        opacity: 0,
        scale: 0.7,
        y: 12
      }} whileInView={{
        opacity: 1,
        scale: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3 + pill.delay,
        type: 'spring',
        stiffness: 240,
        damping: 18
      }} data-id="element-864" data-mp="3knypz0lx3wm8">
            <motion.span className={`block whitespace-nowrap px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-base sm:text-lg font-bold text-white shadow-[0_16px_34px_-12px_rgba(43,33,26,0.45)] ${pill.bg}`} style={{
          rotate: `${pill.rotate}deg`
        }} animate={reduced ? undefined : {
          y: [0, -5, 0]
        }} transition={reduced ? undefined : {
          duration: 4 + pill.delay * 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-865" data-mp="17ghbsj06fz41">
              {pill.text}
            </motion.span>
          </motion.span>)}
      </div>
    </div>;
}
// "Follows through" visual — a continuous left→right carousel of REAL-LOOKING
// deliverable screenshots (a spreadsheet, an analytics chart, a finished-task
// toast, a generated PDF report, and a built slide deck). Each is a tidy little
// app window so the row reads like a stream of finished work drifting by, which
// feels livelier than the old fanned, slowly-floating stack.
function WindowDots() {
  return <span className="flex items-center gap-1 shrink-0" data-id="element-866" data-mp="l70s258j73vp">
      <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" data-id="element-867" data-mp="h6gtcx8b7s7g" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" data-id="element-868" data-mp="c8fdsma3eb0q" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#28C840]" data-id="element-869" data-mp="c8p46az1uk6y" />
    </span>;
}
// Each deliverable card is a fixed-size little app window so the carousel row
// stays even. Built once and reused across the marquee loop.
const FT_CARD_BASE = 'w-[156px] h-[176px] shrink-0 rounded-xl bg-white border border-neutral-200 shadow-[0_18px_40px_-18px_rgba(43,33,26,0.35)] overflow-hidden flex flex-col';
// "An email sent" — a sent-mail confirmation. Reads instantly as a concrete,
// finished task: Giga wrote and sent a real email on your behalf.
function EmailCard() {
  return <div className={FT_CARD_BASE} data-id="element-870" data-mp="1noniat933wf1">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#EA580C] shrink-0" data-id="element-871" data-mp="1nowyjp4bup7j">
        <MailIcon size={10} className="text-white shrink-0" strokeWidth={2.5} data-id="element-872" data-mp="1ivavmonytzox" />
        <span className="text-[8px] font-bold text-white truncate" data-id="element-873" data-mp="1q93bgmax5q08">
          Email sent
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-874" data-mp="1v7n0ivkt7hwt">
        <div className="flex items-center gap-1 text-[7px] text-neutral-400 mb-1" data-id="element-875" data-mp="206hhdrz7uwea">
          <span className="font-semibold text-neutral-500" data-id="element-876" data-mp="208ke0uuvjnrz">To:</span>
          sarah@acmecorp.com
        </div>
        <div className="text-[9.5px] font-bold text-neutral-900 leading-tight mb-1.5" data-id="element-877" data-mp="2975vsnsiuyrp">
          Re: Q3 invoice &amp; next steps
        </div>
        <div className="space-y-1" data-id="element-878" data-mp="2e4o5whi9zk1a">
          <div className="h-1 w-full rounded bg-neutral-100" data-id="element-879" data-mp="2e3middk2p52o" />
          <div className="h-1 w-[94%] rounded bg-neutral-100" data-id="element-880" data-mp="2iy0i1s5rpyd5" />
          <div className="h-1 w-2/3 rounded bg-neutral-100" data-id="element-881" data-mp="2nwbsa3zz6avw" />
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-882" data-mp="2nxmvo2pidgmh">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-883" data-mp="2sw6gu0wa2ods">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-884" data-mp="2wx05wxlgi989" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-885" data-mp="xsibqpzjhtpm">
            Delivered · just now
          </span>
        </div>
      </div>
    </div>;
}
// "A blog post published" — a finished, recognizable creative deliverable.
// Giga drafted the post and pushed it live, end to end.
function BlogCard() {
  return <div className={FT_CARD_BASE} data-id="element-886" data-mp="11s0upg7svupt">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#EA580C] shrink-0" data-id="element-887" data-mp="1gi8290w9rtbq">
        <FileTextIcon size={10} className="text-white shrink-0" strokeWidth={2.5} data-id="element-888" data-mp="1gk1bmny9ornf" />

        <span className="text-[8px] font-bold text-white truncate" data-id="element-889" data-mp="1bive8ctnwg7k">
          Blog published
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-890" data-mp="1pgk09nzgtte7">
        <div className="text-[9.5px] font-bold text-neutral-900 leading-tight mb-1.5" data-id="element-891" data-mp="1lime6u0zrnf1">
          How we cut onboarding to 5 minutes
        </div>
        <div className="space-y-1" data-id="element-892" data-mp="3rc2rfyuc0js5">
          <div className="h-1 w-full rounded bg-neutral-100" data-id="element-893" data-mp="3n9fzlxxnuyuy" />
          <div className="h-1 w-[92%] rounded bg-neutral-100" data-id="element-894" data-mp="3d4prtbfkt0it" />
          <div className="h-1 w-3/4 rounded bg-neutral-100" data-id="element-895" data-mp="3d599qs587owu" />
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-896" data-mp="3h59n09gl9u2b">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-897" data-mp="33exuwak07vb6">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-898" data-mp="38e0x7n8lnnfg" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-899" data-mp="38bocj2bgiyyp">
            Live on the blog
          </span>
        </div>
      </div>
    </div>;
}
function ToastCard() {
  return <div className={`${FT_CARD_BASE} justify-center px-3`} data-id="element-900" data-mp="2yh4mrbz54r89">
      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#0F9D58] shrink-0 mb-2.5" data-id="element-901" data-mp="2kh3pe6i341xj">
        <CheckIcon size={18} strokeWidth={3.5} className="text-white" data-id="element-902" data-mp="2pfy5v1skxeeh" />
      </span>
      <div className="text-[11px] font-bold text-neutral-900 leading-tight" data-id="element-903" data-mp="iofj1oa58ydj">
        Domain connected
      </div>
      <p className="mt-1 text-[9.5px] text-neutral-500 leading-snug" data-id="element-904" data-mp="nmh0y5vu2rnf">
        getgiga.com is live — DNS verified and SSL issued.
      </p>
      <div className="mt-2 flex items-center gap-1" data-id="element-905" data-mp="1wa206txxqpwf">
        <span className="w-1 h-1 rounded-full bg-[#0F9D58]" data-id="element-906" data-mp="1ra5vstlbswgc" />
        <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-907" data-mp="1mieb2zoupky4">
          Completed just now
        </span>
      </div>
    </div>;
}
// "Finances reconciled" — a recognizable back-office outcome: Giga matched the
// month's transactions and balanced the books, end to end.
function FinanceCard() {
  return <div className={FT_CARD_BASE} data-id="element-908" data-mp="18iwj6bg9drkj">
      <div className="flex items-center justify-between px-2 py-1.5 bg-neutral-50 border-b border-neutral-100 shrink-0" data-id="element-909" data-mp="18hv8i835n73c">
        <WindowDots data-id="element-910" data-mp="13jtp1m1th5rc" />
        <span className="text-[7px] font-bold text-neutral-400" data-id="element-911" data-mp="13ld5pdrdxitn">
          Bookkeeping
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-912" data-mp="2xxb5kp3t12cv">
        <div className="text-[10px] font-bold text-neutral-900 leading-tight mb-2" data-id="element-913" data-mp="2xx1f70eukt6n">
          Finances reconciled
        </div>
        <div className="space-y-1.5" data-id="element-914" data-mp="2tu52fqbznram">
          {[{
          label: 'Transactions matched',
          value: '142'
        }, {
          label: 'Flagged for review',
          value: '0'
        }].map(r => <div key={r.label} className="flex items-center justify-between gap-2" data-id="element-915" data-mp="kkyo29h4ljwi">
              <span className="text-[8px] text-neutral-500 truncate" data-id="element-916" data-mp="pg4ixxsia8wq">
                {r.label}
              </span>
              <span className="text-[8px] font-bold text-neutral-800 shrink-0" data-id="element-917" data-mp="thg2pmop7zx0">
                {r.value}
              </span>
            </div>)}
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-918" data-mp="1p8jqflq0bfg">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-919" data-mp="5qajqyngorih">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-920" data-mp="apn6ljec1yl2" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-921" data-mp="1h5g035gimokk">
            Balanced · August
          </span>
        </div>
      </div>
    </div>;
}
// "A meeting booked" — a calendar invite Giga scheduled and confirmed. Another
// concrete, everyday outcome people instantly understand.
function MeetingCard() {
  return <div className={FT_CARD_BASE} data-id="element-922" data-mp="2i5g9o9ezmpv9">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-neutral-50 border-b border-neutral-100 shrink-0" data-id="element-923" data-mp="2i4dh7x20ofd8">
        <CalendarCheckIcon size={10} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-924" data-mp="23e68wa5q4cq9" />

        <span className="text-[8px] font-bold text-neutral-500 truncate" data-id="element-925" data-mp="28dai30sneagl">
          Calendar
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-926" data-mp="1yh7fxuxsqbtn">
        <div className="flex items-center gap-2 mb-2" data-id="element-927" data-mp="1yhyzu8i16l43">
          <div className="flex flex-col items-center justify-center w-9 h-9 rounded-md bg-[#EA580C]/10 shrink-0" data-id="element-928" data-mp="1kho8738wd2hv">
            <span className="text-[6px] font-bold text-[#C2410C] uppercase leading-none" data-id="element-929" data-mp="1pfzlxp2bbx5d">
              Thu
            </span>
            <span className="text-[13px] font-bold text-[#C2410C] leading-none" data-id="element-930" data-mp="3ewqu6qyqm2g1">
              14
            </span>
          </div>
          <div className="min-w-0" data-id="element-931" data-mp="3jsfuiwikznde">
            <div className="text-[10px] font-bold text-neutral-900 leading-tight truncate" data-id="element-932" data-mp="186nj5uirwihg">
              Demo with Acme
            </div>
            <div className="text-[8px] text-neutral-400" data-id="element-933" data-mp="139ndhkpu5hqv">2:00 – 2:30 PM</div>
          </div>
        </div>
        <div className="flex items-center gap-1 mb-2" data-id="element-934" data-mp="1hyb3lbgwwz9f">
          {['#EA580C', '#0F9D58', '#2B211A'].map(c => <span key={c} className="w-3.5 h-3.5 rounded-full border border-white" style={{
          backgroundColor: c
        }} data-id="element-935" data-mp="1d4yu68kt0z19" />)}
          <span className="text-[7px] text-neutral-400 ml-0.5" data-id="element-936" data-mp="1lylijdmon00f">+2 invited</span>
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-937" data-mp="20vueylquetz0">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-938" data-mp="20vcbb41updh2">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-939" data-mp="1vxj6aecaapid" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-940" data-mp="6id1jrk5jdhp">
            Invites sent
          </span>
        </div>
      </div>
    </div>;
}
function FollowsThroughVisual() {
  const reduced = !!useReducedMotion();
  const cards = [<EmailCard key="email" data-id="element-941" data-mp="r98k8ocknfxm" />, <BlogCard key="blog" data-id="element-942" data-mp="v7xs61euc59v" />, <ToastCard key="toast" data-id="element-943" data-mp="v9impr15no7q" />, <FinanceCard key="finance" data-id="element-944" data-mp="v8r21b93jaw8" />, <MeetingCard key="meeting" data-id="element-945" data-mp="107k84im1zzmd" />];
  return <div
  // min-h keeps the band at least as tall as the 176px deliverable cards
  // so, once vertically centered, their tops are never clipped by the
  // overflow-hidden crop when the header copy wraps on narrow screens.
  className="flex-1 relative mt-6 overflow-hidden flex items-center min-h-[196px]" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%)'
  }} data-id="element-946" data-mp="106irzzq6kmxs">
      {reduced ?
    // Reduced motion: a static, horizontally-scrollable row.
    <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-947" data-mp="1sxaluqjrwzhy">
          {cards}
        </div> :
    // Continuous left→right marquee: the track holds two identical sets and
    // animates from -50% to 0% so finished-work cards drift rightward in a
    // seamless loop.
    <motion.div className="flex gap-4 w-max" animate={{
      x: ['-50%', '0%']
    }} transition={{
      duration: 26,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-948" data-mp="3dcp7ux18w4w9">
          {[...cards, ...cards].map((card, i) => cloneElement(card, {
        key: `ft-${i}`
      }))}
        </motion.div>}
    </div>;
}
// "Remembers and repeats" visual — recurring routines Giga runs on a schedule.
// A small stack of "automation" cards, each with a cadence chip (Daily / Mon),
// an ON toggle, and the deliverable it produces — with a third routine peeking
// faintly behind the stack to imply many more running quietly in the background.
const SCHEDULES = [{
  cadence: 'Daily',
  title: 'Daily Sales digest',
  desc: 'New deals, stalls, and what to chase today',
  delay: 0
}, {
  cadence: 'Mon',
  title: 'Weekly Investor Update',
  desc: 'Revenue, burn, pipeline → a clear email draft',
  delay: 0.12
}];
// A small "on" pill toggle — knob pinned right, green track — reading instantly
// as an enabled, recurring automation.
function ScheduleToggle() {
  return <span role="img" aria-label="On" className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-[#0F9D58]" data-id="element-949" data-mp="3pnybsvac8o46">
      <span className="absolute right-0.5 h-4 w-4 rounded-full bg-white shadow-sm" data-id="element-950" data-mp="3unayng17lv6r" />
    </span>;
}
function ScheduleCard({
  s
}: {
  s: (typeof SCHEDULES)[number];
}) {
  const reduced = !!useReducedMotion();
  return <motion.div className="relative w-full rounded-2xl border border-neutral-200/60 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 cursor-default" initial={reduced ? false : {
    opacity: 0,
    y: 14
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={reduced ? {
    duration: 0.2
  } : {
    type: 'spring',
    stiffness: 240,
    damping: 22,
    delay: 0.1 + s.delay
  }} data-id="element-951" data-mp="r59i8ihofc4z">
      <div className="flex items-center justify-between mb-3" data-id="element-952" data-mp="2ukmne2orqoae">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-[#EA580C]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-[#C2410C]" data-id="element-953" data-mp="2pmtj5f7104cr">
          <RepeatIcon size={12} strokeWidth={2.5} data-id="element-954" data-mp="2zdo7qq7qvg1d" />
          {s.cadence}
        </span>
        <ScheduleToggle data-id="element-955" data-mp="2bntuw8nfdrh4" />
      </div>
      <div className="text-sm font-bold text-neutral-900 leading-tight" data-id="element-956" data-mp="2bn2215l7q7uj">
        {s.title}
      </div>
      <p className="mt-1 text-xs text-neutral-500 leading-snug" data-id="element-957" data-mp="26pse5udh248t">{s.desc}</p>
    </motion.div>;
}
function RemembersVisual() {
  return <div className="relative flex-1 w-full mt-5 min-h-0" data-id="element-958" data-mp="2kpuqz26xa7h7">
      {/* The schedule stack is top-aligned and allowed to run past the card's
                                                                                                                                                                         bottom edge, so it reads as an ongoing list of routines rather than two
                                                                                                                                                                         cards centered with awkward dead space. A soft bottom fade dissolves the
                                                                                                                                                                         last card into the card edge (implying more routines running quietly
                                                                                                                                                                         below) and keeps it from butting hard against the container bottom. */}
      <div className="flex h-full w-full items-start justify-center overflow-hidden" data-id="element-959" data-mp="2jte5xgrhwbua">
        {/* On mobile the card is shorter and the header copy is taller, which
         pushes the second routine almost entirely below the fade. Scale the
         stack down a touch on small screens (anchored to the top) so the first
         card stays fully readable and the second peeks through at ~half —
         signaling more routines running below. Full size from sm up. */}
        <div className="w-full max-w-[300px] flex flex-col gap-3 px-1 origin-top scale-[0.82] sm:scale-100" data-id="element-960" data-mp="1aqjm8vy7qv5m">
          {SCHEDULES.map(s => <ScheduleCard key={s.title} s={s} data-id="element-961" data-mp="15s87c6t29ugj" />)}
        </div>
      </div>
      {/* Bottom fade — matches the card's #FAF8F4 surface so the stack softly
                                                                                                                                                                         melts into the lower edge instead of sitting cramped against it. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#FAF8F4] to-transparent" data-id="element-962" data-mp="n3l3tsqj9d52" />
    </div>;
}
// Card 1 copy — the lead line "Does the work you" stays put while the closing
// clause auto-rotates through the three framings of the work Giga takes on, and
// the supporting body copy swaps in sync with each. One source of truth so the
// heading clause and its paragraph can never drift apart.
const ROTATING_WORK = [{
  lead: "can't get to",
  body: 'Giga lives in Slack and soon Teams, your messaging apps, and your email.'
}, {
  lead: "shouldn't have to do",
  body: 'The repetitive busywork that fills your day — chased down, updated, and closed out without you.'
}, {
  lead: "can't justify hiring for",
  body: 'The work that never warranted a new headcount, handled on demand the moment it comes up.'
}];
// --- Automations Carousel Data ---
type AutomationCard = {
  id: string;
  icon: React.ReactNode;
  title: string;
  time: string;
  desc: string;
};
const ALL_CARDS: Record<string, AutomationCard> = {
  morning: {
    id: 'morning',
    icon: <span className="text-2xl" data-id="element-963" data-mp="zbomyny15gn2">☀️</span>,
    title: 'Morning Briefing',
    time: 'Weekdays at 8:00 AM',
    desc: 'Start your day with a summary of your calendar, priority emails, and tasks'
  },
  followup: {
    id: 'followup',
    icon: <span className="text-2xl" data-id="element-964" data-mp="fpefxnajlmsc">🔔</span>,
    title: 'Follow-up Finder',
    time: 'Weekdays at 10:00 AM',
    desc: 'Surface emails, tasks, and meetings that need follow-up action'
  },
  inbox: {
    id: 'inbox',
    icon: <span className="text-2xl" data-id="element-965" data-mp="pn17b99136t7">📥</span>,
    title: 'Inbox Triage',
    time: 'Weekdays at 9:00 AM',
    desc: 'Categorize unread emails by priority and suggest quick responses'
  },
  create: {
    id: 'create',
    icon: <Plus className="text-neutral-400" size={24} data-id="element-966" data-mp="yn7k0swwk2rb" />,
    title: 'Create your own',
    time: '',
    desc: ''
  },
  weekly: {
    id: 'weekly',
    icon: <span className="text-2xl" data-id="element-967" data-mp="1dezkxzvy9e9i">📊</span>,
    title: 'Weekly Review',
    time: 'Fridays at 4:00 PM',
    desc: 'Full week summary - meetings, accomplishments, tasks, and next week preview'
  },
  contact: {
    id: 'contact',
    icon: <span className="text-2xl" data-id="element-968" data-mp="1icrm7kihicqd">🔬</span>,
    title: 'New Contact Research',
    time: 'Weekdays at 8:00 AM',
    desc: 'Research newly added contacts and enrich their profiles'
  },
  pipeline: {
    id: 'pipeline',
    icon: <span className="text-2xl" data-id="element-969" data-mp="1pv3fwbxaihcj">🔍</span>,
    title: 'Pipeline Health Check',
    time: 'Mondays at 9:00 AM',
    desc: 'Review all your CRM pipelines for stale deals, missing info, and next steps'
  },
  network: {
    id: 'network',
    icon: <span className="text-2xl" data-id="element-970" data-mp="1yuqk15hem19a">📰</span>,
    title: 'Network News Digest',
    time: 'Mondays at 8:00 AM',
    desc: 'Weekly news about people and companies in your CRM'
  },
  endofday: {
    id: 'endofday',
    icon: <span className="text-2xl" data-id="element-971" data-mp="1kws9kb67eoz2">🌙</span>,
    title: 'End of Day Digest',
    time: 'Weekdays at 6:00 PM',
    desc: 'Recap what happened today - meetings, emails sent, tasks completed'
  },
  dealflow: {
    id: 'dealflow',
    icon: <span className="text-2xl" data-id="element-972" data-mp="sfftt00awpr7">🔍</span>,
    title: 'Deal Flow Review',
    time: 'Mondays at 9:00 AM',
    desc: 'Review your investment deal pipeline - new deals, decisions needed, follow-ups'
  },
  portfolio: {
    id: 'portfolio',
    icon: <span className="text-2xl" data-id="element-973" data-mp="2z2n7a98bxn1v">📡</span>,
    title: 'Portfolio Company Pulse',
    time: 'Fridays at 9:00 AM',
    desc: 'Weekly news and updates about your portfolio companies'
  },
  lp_pipeline: {
    id: 'lp_pipeline',
    icon: <span className="text-2xl" data-id="element-974" data-mp="2v2wlkjxntx47">🏦</span>,
    title: 'LP Pipeline Review',
    time: 'Mondays at 9:00 AM',
    desc: 'Review your LP fundraising pipeline - commitments, meetings, follow-ups'
  },
  competitor: {
    id: 'competitor',
    icon: <span className="text-2xl" data-id="element-975" data-mp="3mt13mbxy6829">👀</span>,
    title: 'Competitor Watch',
    time: 'Mondays at 8:00 AM',
    desc: 'Weekly monitoring of competitor news, funding, product launches, and hiring'
  },
  content_gen: {
    id: 'content_gen',
    icon: <span className="text-2xl" data-id="element-976" data-mp="3dvh0sykfs3pj">✍️</span>,
    title: 'Content Generator',
    time: 'Sundays at 5:00 PM',
    desc: "Sunday evenings: scans what's happening on X and in the news, reviews your week's meetings and emails..."
  },
  daily_x: {
    id: 'daily_x',
    icon: <span className="text-2xl" data-id="element-977" data-mp="1ibutv0u3nhr2">𝕏</span>,
    title: 'Daily X Ideas',
    time: 'Weekdays at 8:00 AM',
    desc: 'Weekday mornings: surfaces 3 tweet ideas from your meetings, emails, tasks, trending topics, and news...'
  },
  daily_linkedin: {
    id: 'daily_linkedin',
    icon: <span className="text-2xl" data-id="element-978" data-mp="14jwqz7qzei3c">💼</span>,
    title: 'Daily LinkedIn Ideas',
    time: 'Weekdays at 8:00 AM',
    desc: 'Weekday mornings: surfaces 2-3 LinkedIn post concepts from your meetings, emails, tasks, and news...'
  },
  weekly_x: {
    id: 'weekly_x',
    icon: <span className="text-2xl" data-id="element-979" data-mp="ql6qrj8v82ar">𝕏</span>,
    title: 'Weekly X Recap',
    time: 'Mondays at 9:00 AM',
    desc: "Mondays: reviews last week's posts - what performed, what flopped, what formula is working..."
  },
  launch: {
    id: 'launch',
    icon: <span className="text-2xl" data-id="element-980" data-mp="2kxwl2235bdio">📡</span>,
    title: 'Launch Monitor',
    time: 'Every 30 minutes',
    desc: 'Checks your launch posts every 30 minutes - tracks metrics, flags notable engagers, and surfaces...'
  },
  stale_records: {
    id: 'stale_records',
    icon: <span className="text-2xl" data-id="element-981" data-mp="2si77g5c8a8s3">🗂️</span>,
    title: 'Stale Records Cleanup',
    time: 'Fridays at 9:00 AM',
    desc: "Find CRM records that haven't been updated and need attention or archiving"
  },
  pipeline_news: {
    id: 'pipeline_news',
    icon: <span className="text-2xl" data-id="element-982" data-mp="327qwxtug50vx">📰</span>,
    title: 'Pipeline News Roundup',
    time: 'Mondays at 7:00 AM',
    desc: 'Weekly external signal roundup for high-priority pipeline records, including company news, team posts...'
  }
};
const TAB_CONTENT: Record<string, string[]> = {
  Startups: ['morning', 'followup', 'create', 'inbox', 'weekly', 'contact'],
  Investors: ['dealflow', 'portfolio', 'create', 'lp_pipeline', 'network', 'competitor'],
  Content: ['content_gen', 'daily_x', 'create', 'daily_linkedin', 'weekly_x', 'launch'],
  'Email & Messaging': ['morning', 'followup', 'create', 'inbox', 'endofday'],
  CRM: ['pipeline', 'contact', 'create', 'stale_records', 'pipeline_news', 'network'],
  Projects: ['weekly', 'endofday', 'create', 'followup', 'morning', 'inbox']
};
const TABS = Object.keys(TAB_CONTENT);
// Per-tab prompt shown "being typed" inside the "Create your own" card, so the
// example prompt always reads as relevant to the tab the visitor is on.
const CREATE_PROMPTS: Record<string, string> = {
  Startups: 'Summarize my customer emails every morning',
  Investors: 'Draft a weekly update for my LPs',
  Content: 'Turn my meetings into tweet ideas',
  'Email & Messaging': 'Auto-reply to routine emails for me',
  CRM: 'Flag stale deals in my pipeline',
  Projects: 'Recap project progress every Friday'
};
// A looping typewriter: types the given prompt out character by character, holds,
// then clears and repeats — so the card reads as someone actively writing an
// automation. Restarts whenever `resetKey` changes (i.e. when the tab switches).
// Respects reduced motion by rendering the full prompt statically.
function TypewriterPrompt({
  text,
  resetKey
}: {
  text: string;
  resetKey: string;
}) {
  const reduced = !!useReducedMotion();
  const [count, setCount] = useState(reduced ? text.length : 0);
  useEffect(() => {
    if (reduced) {
      setCount(text.length);
      return;
    }
    let cancelled = false;
    let timers: ReturnType<typeof setTimeout>[] = [];
    const run = () => {
      setCount(0);
      let t = 400;
      for (let i = 1; i <= text.length; i++) {
        timers.push(setTimeout(() => {
          if (!cancelled) setCount(i);
        }, t));
        t += 55;
      }
      // Hold the finished prompt, then loop.
      t += 2600;
      timers.push(setTimeout(() => {
        if (!cancelled) run();
      }, t));
    };
    run();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [text, resetKey, reduced]);
  return <span data-id="element-983" data-mp="fyyijono4fte">
      {text.slice(0, count)}
      <motion.span animate={{
      opacity: [1, 0, 1]
    }} transition={{
      duration: 1,
      repeat: Infinity
    }} className="inline-block w-[2px] h-4 bg-[#EA580C] ml-0.5 align-middle" data-id="element-984" data-mp="fxn6l1k2k1r7" />
    </span>;
}
// ------------------------------
function WorkYouCantGetToCopy({
  teamWorkspace = false
}: {
  teamWorkspace?: boolean;
}) {
  const reduced = !!useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => {
      setI(p => (p + 1) % ROTATING_WORK.length);
    }, 3200);
    return () => clearInterval(id);
  }, [reduced]);
  const current = ROTATING_WORK[i];
  return <div className="text-center md:text-left" data-id="element-985" data-mp="2wjk0dz4cibg0">
      <h3 className="text-[1.65rem] sm:text-[1.875rem] md:text-[2.25rem] font-title font-medium text-[#2B211A] mb-3 leading-[1.12]" data-id="element-986" data-mp="2rl8qxphyq2yb">
        {/* Mobile static text */}
        <span className="block md:hidden" data-id="element-987" data-mp="1g6v6yvqby7nk">
          Does the work you  <br data-id="element-988" data-mp="1g520phjmppg3" />
          <span className="text-[#EA580C]" data-id="element-989" data-mp="1g4sdg1pyxwe3">shouldn't have to do</span>
        </span>

        <span className="hidden md:block" data-id="element-990" data-mp="1ba4x4q54c8al">{'\n'}</span>
        <span className="hidden md:block font-[700] pb-[0px] text-[40px] pt-[60px]" data-id="element-991" data-mp="1156bo9j370m7">
          Does the work you
        </span>
        <span className="hidden md:grid overflow-hidden pb-1" data-id="element-992" data-mp="163hl4j5gz93w">
          <AnimatePresence initial={false} data-id="element-993" data-mp="1b216oig5iivq">
            <motion.span key={current.lead} className="col-start-1 row-start-1 block text-[#EA580C] font-[700] text-[40px]" initial={reduced ? false : {
            y: '110%',
            opacity: 0
          }} animate={{
            y: '0%',
            opacity: 1
          }} exit={reduced ? {
            opacity: 0
          } : {
            y: '-110%',
            opacity: 0
          }} transition={reduced ? {
            duration: 0.2
          } : {
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-994" data-mp="1b080f49ga0o9">
              {current.lead}
            </motion.span>
          </AnimatePresence>
        </span>
      </h3>
    </div>;
}
export function BenefitsBento({
  actionHeadline = 'Acts on your behalf',
  actionBody = "Giga doesn't just reply — it does the work and hands back real results.",
  teamWorkspace = false
}: {
  actionHeadline?: React.ReactNode;
  actionBody?: React.ReactNode;
  teamWorkspace?: boolean;
}) {
  return <section className="w-full bg-[#F4F0EA] py-24 md:py-32 border-y border-neutral-200/70 overflow-hidden" data-id="element-995" data-mp="2fzwiwvuh8gdb">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-996" data-mp="2b2mu9ieww8qj">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20" data-id="element-997" data-mp="1nc0q428138lt">
          {/* Eyebrow — a pulsing live dot next to a status label, signaling
           the agent is active and ready to be deployed. */}
          <span className="inline-flex items-center gap-2.5 mb-5 md:mb-6 rounded-full border border-[#EA580C]/20 bg-[#EA580C]/[0.06] pl-2.5 pr-3.5 py-1.5" data-id="element-998" data-mp="1wdgvtwnwemjp">
            <span className="relative flex h-2 w-2 shrink-0" data-id="element-999" data-mp="1sdzqr4cdvrf2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#EA580C] opacity-75" data-id="element-1000" data-mp="37qsttqwz8axy" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#EA580C]" data-id="element-1001" data-mp="33sv50p74bqv3" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#C2410C]" data-id="element-1002" data-mp="3bce382oxnqo9">
              Available immediately
            </span>
          </span>
          <h2 className="text-4xl sm:text-5xl md:text-[56px] font-title text-[#2B211A] tracking-tight mb-5 md:mb-6 font-[500]" data-id="element-1003" data-mp="31df5vg9mbhgk">
            Meet your most capable hire
          </h2>
          <p className="text-lg md:text-xl text-neutral-500 leading-relaxed max-w-2xl mx-auto" data-id="element-1004" data-mp="3u0j4n3i4azjw">
            You ask; it executes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1180px] mx-auto" data-id="element-1005" data-mp="2wcaly6azdx2">
          {/* Card 1: Works where you work — the phone/marquee beat. A real
           iPhone runs a live Slack #growth-team conversation (Marcus flags
           overdue invoices, Faith @mentions Giga, Giga takes it on and hands
           back a result card) over a drifting marquee of the tasks people hand
           Giga. The rotating headline ("Giga does the work you …") sits to the
           left. This restores the original Card 1 that the tabbed Automations
           Carousel had temporarily replaced. */}
          <div className="relative md:col-span-2 bg-[#FAF8F4] rounded-[2rem] border border-neutral-200/60 shadow-sm overflow-hidden h-[400px] sm:h-[440px] md:h-[500px]" data-id="element-1006" data-mp="1xatcngtxagg6">
            {/* Drifting task-pill marquee — sits in the lower band, behind all */}
            <Card1Background data-id="element-1007" data-mp="14np4j2zj9m06" />
            {/* Rotating headline — anchored TOP-LEFT. Wide enough that "Giga
             does the work you" stays on ONE line, so the whole headline reads
             as a clean two-liner (static line + rotating clause). */}
            <div className="relative z-10 px-6 pt-6 sm:px-8 sm:pt-8 md:pl-14 md:pt-14 w-full max-w-full md:max-w-[56%] pb-[100px]" data-id="element-1008" data-mp="1jjy1fxvybn9f">
              <WorkYouCantGetToCopy teamWorkspace={teamWorkspace} data-id="element-1009" data-mp="2pyp9qmnemadh" />
            </div>
            {/* iPhone — large, anchored to the RIGHT, cropped top & bottom by
             the card. Hidden on mobile, where the marquee carries the card. */}
            <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[64%] md:block" data-id="element-1010" data-mp="ec58mqgok1z6">
              <WorksWhereYouWorkVisual data-id="element-1011" data-mp="9lf4fgjzq562" />
            </div>
          </div>

          {/* Card 2: Connects in seconds */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[380px] sm:h-[380px]" data-id="element-1012" data-mp="4mu47h4qnlhm">
            <div className="text-center md:text-left relative z-20" data-id="element-1013" data-mp="3vqirgvw9laia">
              <h3 className="text-2xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-1014" data-mp="3rq8ntpvy2w6l">
                Self onboards in seconds
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-1015" data-mp="3ht3vt9ff46hv">
                Once connected, Giga learns about your company.
              </p>
            </div>
            <ConnectsVisual data-id="element-1016" data-mp="1kyhkmai6gspp" />
          </div>

          {/* Card 3: Acts like a partner / Team Workspace automation */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[380px] sm:h-[380px]" data-id="element-1017" data-mp="3ahpk0a54h2q9">
            <div className="text-center md:text-left relative z-20" data-id="element-1018" data-mp="3f9z61f349in9">
              <h3 className="text-2xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-1019" data-mp="30kb3kaj78dxy">
                {actionHeadline}
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-1020" data-mp="35gib27r8txi0">
                {actionBody}
              </p>
            </div>
            {teamWorkspace ? <RemembersVisual data-id="element-1021" data-mp="225unm7ko4gh" /> : <PartnerVisual data-id="element-1022" data-mp="20l0hxp66nj5" />}
          </div>

          {/* Card 4: Follows through */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[380px] sm:h-[380px]" data-id="element-1023" data-mp="3j8zlcnmdy35i">
            <div className="text-center md:text-left relative z-20" data-id="element-1024" data-mp="27uudvkmyrb5f">
              <h3 className="text-2xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-1025" data-mp="27tjcfrgzqfhh">
                Never out of office
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-1026" data-mp="uvstm3prcf6m">
                A colleague who's always on — replying, nudging, and noticing,
                even when you're not.
              </p>
            </div>
            <FollowsThroughVisual data-id="element-1027" data-mp="13vz5xm9pz947" />
          </div>

          {/* Card 5: Built for teams */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[380px] sm:h-[380px]" data-id="element-1028" data-mp="zwgl0qhwey1d">
            <div className="text-center md:text-left relative z-20" data-id="element-1029" data-mp="76q4ybwphm2m">
              <h3 className="text-2xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-1030" data-mp="78kr1tjprk8a">
                Remembers and repeats
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-1031" data-mp="g6wczi9mxtue">
                Set the routine once. Giga remembers it and delivers on schedule
                — without being asked twice.
              </p>
            </div>
            {teamWorkspace ? <PartnerVisual data-id="element-1032" data-mp="3kle1xm1j8a0m" /> : <RemembersVisual data-id="element-1033" data-mp="3kjthwqcug9h0" />}
          </div>
        </div>
      </div>
    </section>;
}